import { useTranslation } from "@sagot/i18n";
import { Button, Icon, cn } from "@sagot/ui";
import type { ReactNode } from "react";

interface FormAlertProps {
  title: ReactNode;
  children?: ReactNode;
  tone?: "danger" | "warning" | "info";
  onDismiss?: () => void;
  className?: string;
}

const TONE = {
  danger: { box: "bg-danger-soft", icon: "alert-circle", text: "text-danger" },
  warning: { box: "bg-warning-soft", icon: "alert-triangle", text: "text-warning" },
  info: { box: "bg-primary-soft", icon: "info", text: "text-primary-ink" },
} as const;

/** Inline API error (login.html `#login-error`): icon + bold title + body, dismissible. */
export function FormAlert({ title, children, tone = "danger", onDismiss, className }: FormAlertProps) {
  const { t } = useTranslation();
  const style = TONE[tone];
  return (
    <div role="alert" className={cn("flex items-start gap-2.5 rounded-md border border-line px-3 py-2.5", style.box, className)}>
      <Icon name={style.icon} className={cn("mt-0.5 size-4", style.text)} />
      <div className="flex-1 text-xs text-fg">
        <strong className={cn("block", style.text)}>{title}</strong>
        {children}
      </div>
      {onDismiss ? (
        <Button variant="ghost" size="sm" iconOnly aria-label={t("auth.common.dismiss")} className="-mr-1 -mt-1" onClick={onDismiss}>
          <Icon name="x" />
        </Button>
      ) : null}
    </div>
  );
}
